import React from 'react';

import { IProductTemplateAttribute } from 'components/dashboard/product-template-table/utils/interfaces';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { ConfirmPopup, confirmPopup } from 'primereact/confirmpopup';
import { DataTable } from 'primereact/datatable';
import { CellType } from 'utils/enums';
import { Tooltip } from 'react-tooltip';
import ProductTemplateAttributeDialog from './ProductTemplateAttributeDialog';

type ProductTemplateAttributeListProps = {
	attributes: IProductTemplateAttribute[];
	onAttributesChange: (newAttributes: IProductTemplateAttribute[]) => void;
};

const defaultAttribute: IProductTemplateAttribute = {
	displayName: 'Display name',
	type: CellType.STRING,
	referenceName: 'reference_name'
} 

const ProductTemplateAttributeList: React.FC<
	ProductTemplateAttributeListProps
> = ({ attributes, onAttributesChange }) => {
	const attributesCopy = [...attributes];
	
	const confirmOnDelete = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		index: number,
	) => {
		confirmPopup({
			target: e.currentTarget,
			message: 'Are you sure you want to delete this column?',
			icon: 'pi pi-exclamation-triangle',
			acceptClassName: 'p-button-danger',
			accept: () => deleteColumn(index),
		});
	};

	const deleteColumn = (index: number) => {
		attributesCopy.splice(index, 1);
		onAttributesChange(attributesCopy);
	};

	const updateAttributes = (
		newAttribute: IProductTemplateAttribute,
		index: number,
	) => {
		attributesCopy.splice(index, 1, newAttribute);
		onAttributesChange(attributesCopy);
	};

	return (
		<>
			<ConfirmPopup />
			<Accordion className='p-field p-col-12'>
				<AccordionTab header='Attributes'>
					<DataTable
						value={attributes}
						size='small'
						editMode='row'
						reorderableRows
						onRowReorder={e => onAttributesChange(e.value)}>
						<Column field='displayName' header='Column Name' />
						<Column field='referenceName' header='Reference Name' />
						<Column field='type' header='Type' />
						<Column
							style={{ width: '0' }}
							body={(rowData, options) => (
								<ProductTemplateAttributeDialog
									index={options.rowIndex}
									attribute={rowData}
									onAttributeChange={updateAttributes}
                  attributes={attributes}
								/>
							)}
						/>
						<Column
							style={{ width: '0' }}
							body={(_, options) => (
								<Button
										icon='pi pi-trash'
										className='p-button-sm p-button-danger'
										onClick={e => confirmOnDelete(e, options.rowIndex)}
										disabled={attributes.length === 1}
									/>
							)}
						/>
						<Column rowReorder style={{ width: '3em' }} />
					</DataTable>

					<div className='flex mt-3'>
						<Tooltip id='add-attribute-btn' />
						<Button
							data-testid='add-attribute-btn'
							data-tooltip-id='add-attribute-btn'
							data-tooltip-content='Add Attribute'
							data-tooltip-place='right'
							icon='pi pi-plus'
							className='p-button-outlined p-button-rounded p-button-success mt-3 ml-3'
							onClick={() =>
								onAttributesChange([...attributes, defaultAttribute])
							}
						/>
					</div>
				</AccordionTab>
			</Accordion>
		</>
	);
};

export default ProductTemplateAttributeList;
